import React, {useState, useEffect, lazy, Suspense} from 'react';
import * as Sentry from "@sentry/react";
import Desktop from './Layouts/Desktop';
import Mobile from './Layouts/Mobile';
import { Navigate, RouterProvider, createBrowserRouter, useParams } from 'react-router-dom';
import Rounds from './Pages/Rounds';
import Calendar from './Pages/Calendar';
import Guest from './Layouts/Guest';
import Login from './Pages/Auth/Login';
import Register from './Pages/Auth/Register';
import ToastsWrapper from './Components/Toasts';
import Home from './Pages/Home';
import Customers from './Pages/Customers';
import Session, { useSession } from './Components/Session';
import Customer from './Pages/Customer';
import Round from './Pages/Round';
import Quotes from './Pages/Quotes';
import Quote from './Pages/Quote';
import Itemisation from './Pages/Itemisation';
import Work from './Pages/Work';
import More from './Pages/More/More';
import Help from './Pages/Help';
import Question from './Pages/Help/Question';
import Details from './Pages/Business/Details';
import CustomSMSName from './Pages/Business/CustomSMSName';
import EditService from './Pages/PricingAndServices/Services/Edit';
import Profile from './Pages/User/Profile';
import CloseAccount from './Pages/User/CloseAccount';
import Notifications from './Pages/User/Notifications';
import CustomerNotifications from './Pages/More/Customers/Notifications';
import MessageTemplate from './Pages/More/Customers/MessageTemplate';
import MessageTemplates from './Pages/More/Customers/MessageTemplates';
import QuoteOptions from './Pages/More/Customers/QuoteOptions';
import DocumentTemplates from './Pages/More/Customers/DocumentTemplates';
import Leads from './Pages/Leads';
import ForgotPassword from './Pages/Auth/ForgotPassword';
import ResetPassword from './Pages/Auth/ResetPassword';
import Jobs from './Pages/Jobs';
import Authenticating from './Pages/Auth/Authenticating';
import Setup from './Pages/More/Payments/Setup';
import BankAccount from './Pages/More/Payments/BankAccount';
import PaymentOptions from './Pages/More/Payments/PaymentOptions';
import BankReconciliation from './Pages/More/Payments/BankReconciliation';
import Expenses from './Pages/Expenses';
import Payouts from './Pages/More/Payments/Payouts';
import Imports from './Pages/Imports';
import AdditionalUsers from './Pages/Business/AdditionalUsers';
import RedirectShortLink from './Pages/RedirectShortLink';
import WeekRedirect from './Pages/Calendar/WeekRedirect';
import Tags from './Pages/More/Customers/Tags';
import Invoices from './Pages/Invoices';
import SocketWrapper, { useSocketListener } from './Components/Socket';
import { deviceIsMd } from './Services/helpers';

const Billing = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Billing'));
const Plan = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Billing/Plan'));
const Payments = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Payments'));
const PricingAndServices = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/PricingAndServices'));
const Documents = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Documents'));
const Document = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Document'));
const Broadcasts = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Broadcasts'));
const Broadcast = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Broadcast'));
const WhatsNew = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/WhatsNew'));
const Socials = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/Socials'));
const ReferAFriend = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/ReferAFriend'));
const Verification = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Verification'));
const CommunicationPreferences = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/More/CommunicationPreferences'));

const Reports = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports'));
const Overview = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Overview'));
const JobsReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Jobs'));
const InvoicesReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Invoices'));
const RoundsReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Rounds'));
const ExpensesReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Expenses'));
const PaymentsReport = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Reports/Payments'));
const ChangeType = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/ChangeType'));
const Theme = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Theme'));
const Links = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Links'));
const ThirdPartySettings = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/ThirdPartySettings'));
const Content = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Content'));
const WidgetOptions = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/WidgetOptions'));
const CustomDomain = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/CustomDomain'));
const Stats = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Website/Stats'));
const Videos = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Help/Videos'));
const Checklist = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Pages/Help/Checklist'));

// Admin
const Admin = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Admin'));
const Businesses = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Businesses'));
const Revenue = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Revenue'));
const Dial = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Dial'));
const Experiments = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/Experiments'));
const ScratchPad = lazy(() => import(/* webpackChunkName: "admin" */'./Pages/Admin/ScratchPad'));

const App = () => {

    const [waitingForCss, setWaitingForCss] = useState(true);
    const [isDesktop, setIsDesktop] = useState(deviceIsMd());

    useEffect(() => {
        import(/* webpackLoad: true */'./css/app.scss').then(() => {
            setWaitingForCss(false);
            document.getElementById('loading-container')?.remove();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const resize = () => setIsDesktop(deviceIsMd());

        window.addEventListener('resize', resize);

        return () => window.removeEventListener('resize', resize);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (waitingForCss) {
        return null;
    }

    return (
        <ToastsWrapper>
            <Session>
                <SocketWrapper>
                    <MakeRouter isDesktop={isDesktop} />
                    <NotificationSubscription />
                </SocketWrapper>
            </Session>
        </ToastsWrapper>
    )
}

export default Sentry.withProfiler(App);

const NotificationSubscription = () => {
    const {journal, fetchNotifications, setNotificationBadge} = useSession();

    useSocketListener(
        journal ? {entity: {entityModel: 'Notifications/Journal', entityId: journal.journalId}} : null,
        (data) => {
            setNotificationBadge(data.unreadNotifications);
            fetchNotifications();
        },
    );

    return null;
}

const MakeRouter = ({isDesktop}) => {
    const {hasToken, isReady} = useSession();

    return <RouterProvider router={hasToken && isReady ? makeAuthedRouter(isDesktop) : makeGuestRouter(hasToken)} />
}

const makeAuthedRouter = (isDesktop) => {
    return createBrowserRouter([
        {
            path: '/',
            element: isDesktop ? <Desktop /> : <Mobile />,
            children: [
                {
                    index: true,
                    element: <Home />,
                },
                {
                    path: '/work',
                    element: <Navigate to={'/work/'+(new Date().toISOString().slice(0, 10))} />,
                },
                {
                    path: '/work/:date',
                    element: <Work />,
                },
                {
                    path: '/plan/calendar',
                    element: <WeekRedirect />,
                },
                {
                    path: '/plan/calendar/:week',
                    element: <Calendar />,
                },
                {
                    path: '/plan/rounds',
                    element: <Rounds />,
                },
                {
                    path: '/plan/rounds/:roundId',
                    element: <Round />,
                },
                {
                    path: '/plan/jobs',
                    element: <Jobs />,
                },
                {
                    path: '/plan/leads',
                    element: <Leads />,
                },
                {
                    path: '/plan/leads/:leadId',
                    element: <Leads />,
                },
                {
                    path: '/manage/customers',
                    element: <Customers />,
                },
                {
                    path: '/manage/customers/:customerId',
                    element: <Customer />
                },
                {
                    path: '/manage/quotes',
                    element: <Quotes />
                },
                {
                    path: '/manage/quotes/:quoteId',
                    element: <Quote />
                },
                {
                    path: '/manage/:itemType/:itemId/itemisations/:itemisationId',
                    element: <Itemisation />
                },
                {
                    path: '/manage/payments',
                    element: <Suspense><Payments /></Suspense>
                },
                {
                    path: '/manage/payments/:paymentId',
                    element: <Suspense><Payments /></Suspense>
                },
                {
                    path: '/manage/invoices',
                    element: <Invoices />
                },
                {
                    path: '/manage/expenses',
                    element: <Expenses />
                },
                {
                    path: '/more/broadcasts',
                    element: <Suspense><Broadcasts /></Suspense>
                },
                {
                    path: '/more/broadcasts/:broadcastId',
                    element: <Suspense><Broadcast /></Suspense>
                },
                {
                    path: '/more',
                    element: <More />
                },
                {
                    path: '/business/details',
                    element: <Details />
                },
                {
                    path: '/business/custom-sms-name',
                    element: <CustomSMSName />
                },
                {
                    path: '/business/services',
                    element: <Suspense><PricingAndServices /></Suspense>
                },
                {
                    path: '/business/services/:serviceId',
                    element: <EditService />
                },
                {
                    path: '/business/documents',
                    element: <Suspense><Documents /></Suspense>,
                },
                {
                    path: '/business/documents/:documentId',
                    element: <Suspense><Document /></Suspense>,
                },
                {
                    path: '/business/additional-users',
                    element: <AdditionalUsers />
                },
                {
                    path: '/user/profile',
                    element: <Profile />
                },
                {
                    path: '/verification',
                    element: <Suspense><Verification /></Suspense>
                },
                {
                    path: '/user/notification-preferences',
                    element: <Notifications />
                },
                {
                    path: '/user/close-account',
                    element: <CloseAccount />
                },
                {
                    path: '/settings/customer-notifications',
                    element: <CustomerNotifications />
                },
                {
                    path: '/settings/message-templates/:templateId',
                    element: <MessageTemplate />
                },
                {
                    path: '/settings/document-templates',
                    element: <DocumentTemplates />
                },
                {
                    path: '/settings/quote-options',
                    element: <QuoteOptions />
                },
                {
                    path: '/settings/message-templates',
                    element: <MessageTemplates />
                },
                {
                    path: '/settings/import',
                    element: <Imports />
                },
                {
                    path: '/settings/tags',
                    element: <Tags />
                },
                {
                    path: '/settings/payments/setup',
                    element: <Setup />
                },
                {
                    path: '/settings/payments/bank-account',
                    element: <BankAccount />
                },
                {
                    path: '/settings/payments/options',
                    element: <PaymentOptions />
                },
                {
                    path: '/settings/payments/bank-reconciliation',
                    element: <BankReconciliation />
                },
                {
                    path: '/settings/payments/payouts',
                    element: <Payouts />
                },
                {
                    path: '/website/type',
                    element: <Suspense><ChangeType /></Suspense>,
                },
                {
                    path: '/website/theme',
                    element: <Suspense><Theme /></Suspense>,
                },
                {
                    path: '/website/links',
                    element: <Suspense><Links /></Suspense>,
                },
                {
                    path: '/website/third-party-settings',
                    element: <Suspense><ThirdPartySettings /></Suspense>,
                },
                {
                    path: '/website/content',
                    element: <Suspense><Content /></Suspense>,
                },
                {
                    path: '/website/widget-options',
                    element: <Suspense><WidgetOptions /></Suspense>,
                },
                {
                    path: '/website/custom-domain',
                    element: <Suspense><CustomDomain /></Suspense>,
                },
                {
                    path: '/website/stats',
                    element: <Suspense><Stats /></Suspense>,
                },
                {
                    path: '/billing',
                    element: <Suspense><Billing /></Suspense>
                },
                {
                    path: '/billing/plan',
                    element: <Suspense><Plan /></Suspense>
                },
                {
                    path: '/help/videos',
                    element: <Suspense><Videos /></Suspense>
                },
                {
                    path: '/help/checklist',
                    element: <Suspense><Checklist /></Suspense>
                },
                {
                    path: '/help',
                    element: <Help />
                },
                {
                    path: '/help/question',
                    element: <Question />
                },
                {
                    path: '/help/question/:question',
                    element: <Question />
                },
                {
                    path: '/reports',
                    element: <Suspense><Reports /></Suspense>,
                },
                {
                    path: '/reports/overview',
                    element: <Suspense><Overview /></Suspense>,
                },
                {
                    path: '/reports/jobs',
                    element: <Suspense><JobsReport /></Suspense>,
                },
                {
                    path: '/reports/invoices',
                    element: <Suspense><InvoicesReport /></Suspense>,
                },
                {
                    path: '/reports/rounds',
                    element: <Suspense><RoundsReport /></Suspense>,
                },
                {
                    path: '/reports/expenses',
                    element: <Suspense><ExpensesReport /></Suspense>,
                },
                {
                    path: '/reports/payments',
                    element: <Suspense><PaymentsReport /></Suspense>,
                },
                {
                    path: '/whats-new',
                    element: <Suspense><WhatsNew /></Suspense>
                },
                {
                    path: '/get-social',
                    element: <Suspense><Socials /></Suspense>
                },
                {
                    path: '/more/communication-preferences',
                    element: <Suspense><CommunicationPreferences /></Suspense>
                },
                {
                    path: '/refer-a-friend',
                    element: <Suspense><ReferAFriend /></Suspense>
                },
                {
                    path: '/redirect-short-link/:code',
                    element: <RedirectShortLink />,
                },
                {
                    path: '/admin',
                    element: <Suspense><Admin /></Suspense>,
                },
                {
                    path: '/admin/businesses',
                    element: <Suspense><Businesses /></Suspense>,
                },
                {
                    path: '/admin/revenue',
                    element: <Suspense><Revenue /></Suspense>,
                },
                {
                    path: '/admin/dial/:number',
                    element: <Suspense><Dial /></Suspense>,
                },
                {
                    path: '/admin/dial',
                    element: <Suspense><Dial /></Suspense>,
                },
                {
                    path: '/admin/experiments',
                    element: <Suspense><Experiments /></Suspense>,
                },
                {
                    path: '/admin/scratch-pad',
                    element: <Suspense><ScratchPad /></Suspense>,
                },
                // Legacy redirects
                ...redirects({
                    '/balance': '/billing',
                    '/business/brand': '/business/details',
                    '/business/socials': '/business/details',
                    '/calendar': '/plan/calendar',
                    '/customers': '/manage/customers',
                    '/customers/:id': '/manage/customers/:id',
                    '/expenses': '/manage/expenses',
                    '/marketplace': '/plan/leads',
                    '/marketplace/lead/:id': '/plan/leads/:id',
                    '/more/reports': '/reports',
                    '/payments': '/manage/payments',
                    '/payments/view/:id': '/manage/payments/:id',
                    '/quotes': '/manage/quotes',
                    '/quotes/:id': '/manage/quotes/:id',
                    '/rounds': '/plan/rounds',
                    '/services': '/business/services',
                    '/settings': '/more',
                    '/settings/customers/notifications': '/settings/customer-notifications',
                    '/settings/payments/connected-banks': '/settings/payments/bank-reconciliation',
                    '/settings/payments/connected-banks/:connectedBankId': '/settings/payments/bank-reconciliation',
                    '/settings/pricing/categories': '/business/services',
                }),
                {
                    path: '*',
                    element: <Navigate to='/' />,
                },
                // {
                //     path: '/customers/:customerId',
                //     element: <Redirect to='/manage/customers/' />,
                // }
            ]
        },
    ])
}

const redirects = (redirs) => {
    return Object.keys(redirs).map(from => {
        if (from.indexOf(':id') > -1) {
            return {
                path: from,
                element: <HandleRedirect to={redirs[from]} />,
            };
        }
        return {
            path: from,
            element: <Navigate to={redirs[from]} />,
        };
    });
}

const HandleRedirect = ({to}) => {
    const {id} = useParams();

    return <Navigate to={to.replace(':id', id)} />
}

const makeGuestRouter = (hasToken) => {
    return createBrowserRouter([
        {
            path: '/',
            element: <Guest />,
            children: [
                {
                    index: true,
                    element: hasToken ? <Authenticating /> : <Navigate to='/login' />,
                },
                {
                    path: '/login',
                    element: <Login />,
                },
                {
                    path: '/forgotten-password',
                    element: <ForgotPassword />,
                },
                {
                    path: '/forgotten-password/reset/:hash',
                    element: <ResetPassword />,
                },
                {
                    path: '/register/:code',
                    element: <Register />,
                },
                {
                    path: '/register',
                    element: <Register />,
                },
                {
                    path: '*',
                    element: hasToken ? <Authenticating /> : <Navigate to='/login' />,
                }
            ]
        },
    ])
}
